// Theme variables for a modern, clean UI inspired by Portugal's colors and landscapes
export const colors = {
  // Primary colors - Inspired by Portuguese azulejos (tiles) and the Atlantic
  primary: "#0A6E9C",      // Vibrant azure blue - like the Atlantic Ocean
  secondary: "#FF7B54",    // Warm terracotta - traditional Portuguese tiles
  accent: "#FFD056",       // Golden yellow - like the Algarve beaches
  
  // Background and surfaces
  background: "#F9FAFB",   // Light neutral background
  card: "#FFFFFF",         // Pure white for cards
  cardHover: "#F0F7FF",    // Subtle hover state for cards
  border: "#E2E8F0",       // Light border color
  divider: "#EDF2F7",      // Even lighter divider
  
  // Text colors
  text: "#1A202C",         // Dark blue-gray for primary text
  textSecondary: "#4A5568", // Secondary text
  muted: "#718096",        // Muted gray for tertiary text
  
  // Accent colors
  highlight: "#F0F9FF",    // Soft blue highlight
  success: "#38A169",      // Green for success states
  warning: "#F59E0B",      // Orange for warnings
  error: "#E53E3E",        // Red for errors
  info: "#3182CE",         // Blue for info
  
  // Additional Portuguese-inspired colors
  azure: "#0A6E9C",        // Azure blue
  terracotta: "#E07A5F",   // Warm terracotta
  olive: "#8A9B68",        // Olive green - like the countryside
  sand: "#E6C79C",         // Sand beige - like Algarve beaches
  lavender: "#805AD5",     // Lavender - like the fields of Alentejo
  teal: "#319795",         // Teal - like the coastal waters
  
  // Gradients
  gradientPrimary: 'linear-gradient(135deg, #0A6E9C 0%, #3182CE 100%)',
  gradientSecondary: 'linear-gradient(135deg, #FF7B54 0%, #FF9A76 100%)',
  gradientAccent: 'linear-gradient(135deg, #FFD056 0%, #FFBE2E 100%)',
  gradientCool: 'linear-gradient(135deg, #0A6E9C 0%, #319795 100%)',
  gradientWarm: 'linear-gradient(135deg, #E07A5F 0%, #FF9A76 100%)',
  
  // Social colors
  facebook: '#1877F2',
  instagram: '#E1306C',
  twitter: '#1DA1F2',
  whatsapp: '#25D366'
};

// Design tokens for consistent spacing, typography, and effects
export const spacing = {
  xs: '0.25rem',   // 4px
  sm: '0.5rem',    // 8px
  md: '1rem',      // 16px
  lg: '1.5rem',    // 24px
  xl: '2rem',      // 32px
  xxl: '3rem'      // 48px
};

export const typography = {
  fontFamily: {
    base: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    heading: "'Playfair Display', serif"
  },
  fontSize: {
    xs: '0.75rem',    // 12px
    sm: '0.875rem',   // 14px
    md: '1rem',       // 16px
    lg: '1.125rem',   // 18px
    xl: '1.25rem',    // 20px
    xxl: '1.5rem',    // 24px
    h1: '2.5rem',     // 40px
    h2: '2rem',       // 32px
    h3: '1.5rem',     // 24px
    h4: '1.25rem'     // 20px
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700
  },
  lineHeight: {
    tight: 1.2,
    base: 1.5,
    relaxed: 1.75
  }
};

export const effects = {
  borderRadius: {
    sm: '0.25rem',    // 4px
    md: '0.5rem',     // 8px
    lg: '1rem',       // 16px
    xl: '1.5rem',     // 24px
    full: '9999px'    // Fully rounded
  },
  boxShadow: {
    sm: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
    md: '0 4px 6px rgba(0,0,0,0.1), 0 2px 4px rgba(0,0,0,0.06)',
    lg: '0 10px 15px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)',
    xl: '0 20px 25px rgba(0,0,0,0.1), 0 10px 10px rgba(0,0,0,0.04)'
  },
  transition: {
    fast: 'all 0.2s ease',
    base: 'all 0.3s ease',
    slow: 'all 0.5s ease'
  }
};

// Breakpoints for responsive design
export const breakpoints = {
  xs: '480px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  xxl: '1536px'
};

// Complete theme object
export const theme = {
  colors,
  spacing,
  typography,
  effects,
  breakpoints
};
